import React, { useState, useEffect, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import MenuItem from './MenuItem';
import './Sidebar.css';
import API from '../../utils/API.js';
import constants from '../../utils/constants';
import { fetchLicensesSuccess } from '../../store/licenses';
import { setUnsavedChanges } from '../../store/signage';
import NotificationModal from '../MessageManager/NotificationModal';
// SIDEBAR COLLAPSED ICONS
import { ReactComponent as DashboardIcon } from '../../assets/icons/sidebar/layout.svg';
import { ReactComponent as UsersIcon } from '../../assets/icons/sidebar/users.svg';
import { ReactComponent as LicenseIcon } from '../../assets/icons/sidebar/license-management.svg';
import { ReactComponent as AccountIcon } from '../../assets/icons/sidebar/user.svg';
import { ReactComponent as CmsIcon } from '../../assets/icons/sidebar/cms-management.svg';
import { ReactComponent as AlertPlusIcon } from '../../assets/icons/sidebar/alert-plus.svg';
import { ReactComponent as RaptorIcon } from '../../assets/icons/sidebar/shield.svg';
import { ReactComponent as OrchestrateIcon } from '../../assets/icons/sidebar/orchestrate.svg';
import { ReactComponent as MessageManagerIcon } from '../../assets/icons/sidebar/message-manager.svg';
import { ReactComponent as HardwareWarrantyIcon } from '../../assets/icons/sidebar/hardware-warranty.svg';
import { ReactComponent as RolesIcon } from '../../assets/icons/sidebar/user.svg';
import { ReactComponent as TrialProvisioningIcon } from '../../assets/icons/sidebar/info.svg';
import { ReactComponent as SupportIcon } from '../../assets/icons/sidebar/info.svg';
import { ReactComponent as MiscellaneousIcon } from '../../assets/icons/sidebar/more-horizontal.svg';
import { ReactComponent as CollapseIcon } from '../../assets/icons/sidebar/collapse.svg';
import { ReactComponent as ExpandIcon } from '../../assets/icons/sidebar/collapse.svg';
import { ReactComponent as SignagePlusIcon } from '../../assets/icons/sidebar/signage-plus.svg';
import ScheduledMessages from '../../assets/icons/message-manager/scheduledMessages.svg';
import Save from '../../assets/icons/signage-plus/save.svg';

const Sidebar = (props) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, setIsSidebarOpen, isSidebarOpen, licenses, signage } = props;
  const permissionIds = user.permissions.map((permission) => permission.id);
  const [selected, setSelected] = useState('');
  const [featureLicenses, setFeatureLicenses] = useState({
    AlertPlus: false,
    Orchestrate: false
  });
  const modalRef = useRef(null);
  const [notificationProps, setNotificationProps] = useState({
    icon: ScheduledMessages,
    id: 'sidebarNotificationModal',
    message: 'SIGNAGE_SCHEDULE_MESSAGE',
    description: 'SIGNAGE_SCHEDULE_WARNING',
    primaryButtonText: 'EXIT',
    secondaryButtonText: 'SCHEDULE_SIGNAGE',
    showSecondaryButton: true,
    className: 'green',
    onClose: () => { modalRef.current.hide() }
  });

  const menuStructure = {
    dashboard: {
      id: 'dashboardTab',
      label: <FormattedMessage id="DASHBOARD" defaultMessage="Dashboard" />,
      path: '',
      permission: null,
      icon: DashboardIcon
    },
    userManagement: {
      id: 'userManagementTab',
      label: <FormattedMessage id="USER_MANAGEMENT" defaultMessage="User Management" />,
      path: 'user-management',
      permission: constants.permissionIds.userEdit,
      icon: UsersIcon
    },
    accountManagement: {
      id: 'accountManagementTab',
      label: <FormattedMessage id="ACCOUNT_MANAGEMENT" defaultMessage="Account Management" />,
      path: 'account-management',
      permission: constants.permissionIds.support,
      icon: AccountIcon
    },
    licenseManagement: {
      id: 'licenseManagementTab',
      label: <FormattedMessage id="LICENSE_MANAGEMENT" defaultMessage="License Management" />,
      path: 'license-management',
      permission: constants.permissionIds.licensesEdit,
      icon: LicenseIcon
    },
    cmsManagement: {
      id: 'cmsManagementTab',
      label: <FormattedMessage id="CMS_MANAGEMENT" defaultMessage="CMS Management" />,
      path: 'cms',
      permission: constants.permissionIds.cmsEdit,
      icon: CmsIcon
    },
    cmsManagementSupport: {
      id: 'cmsManagementSupportTab',
      label: <FormattedMessage id="CMS_MANAGEMENT" defaultMessage="CMS Management" />,
      path: 'cms-support',
      permission: constants.permissionIds.support,
      icon: CmsIcon
    },
    hardwareWarranty: {
      id: 'hardwareWarrantyTab',
      label: <FormattedMessage id="HARDWARE_WARRANTY" defaultMessage="Hardware Warranty" />,
      path: 'hardware-warranty',
      permission: constants.permissionIds.hwWarrantyRead,
      icon: HardwareWarrantyIcon
    },
    // userRoles: {
    //   id: 'userRolesTab',
    //   label: <FormattedMessage id="USER_ROLES" defaultMessage="User Roles" />,
    //   path: 'roles',
    //   permission: constants.permissionIds.userEdit,
    //   icon: RolesIcon
    // },
    alertPlus: {
      id: 'alertPlusTab',
      label: <FormattedMessage id="ALERT_PLUS" defaultMessage="Alert Plus" />,
      path: '',
      permission: [constants.permissionIds.aPlusEdit, constants.permissionIds.aPlusOfficerApp],
      icon: AlertPlusIcon,
      requiresLicense: 'AlertPlus',
      submenu: {
        manage: {
          id: 'alertPlusManageTab',
          label: <FormattedMessage id="ALERT_PLUS_MANAGE" defaultMessage="Manage" />,
          path: 'alert-plus/manage',
          permission: constants.permissionIds.aPlusOfficerApp
        },
        logs: {
          id: 'alertPlusLogsTab',
          label: <FormattedMessage id="ALERT_PLUS_LOGS" defaultMessage="Logs" />,
          path: 'alert-plus/logs',
          permission: constants.permissionIds.aPlusOfficerApp
        },
        users: {
          id: 'alertPlusUsersTab',
          label: <FormattedMessage id="ALERT_PLUS_USERS" defaultMessage="Users" />,
          path: 'alert-plus/users',
          permission: constants.permissionIds.aPlusEdit
        },
        templates: {
          id: 'alertPlusTemplatesTab',
          label: <FormattedMessage id="TEMPLATES" defaultMessage="Templates" />,
          path: 'alert-plus/templates',
          permission: constants.permissionIds.aPlusEdit
        },
        // apiKey: {
        //   id: 'apiKeyTab',
        //   label: <FormattedMessage id="API_KEY" defaultMessage="API Key" />,
        //   path: 'alert-plus/api-key',
        //   permission: [constants.permissionIds.aPlusOfficerApp, constants.permissionIds.aPlusEdit]
        // },
        // siteMapping: {
        //   id: 'siteMappingTab',
        //   label: <FormattedMessage id="SITE_MAPPING" defaultMessage="Site Mapping" />,
        //   path: 'alert-plus/site-mapping',
        //   permission: [constants.permissionIds.aPlusOfficerApp, constants.permissionIds.aPlusEdit]
        // }
      }
    },
    raptor: {
      id: 'raptorTab',
      label: <FormattedMessage id="RAPTOR" defaultMessage="Raptor" />,
      path: '',
      permission: constants.permissionIds.aPlusEdit,
      icon: RaptorIcon,
      requiresLicense: 'AlertPlus',
      submenu: {
        configuration: {
          id: 'configurationTab',
          label: <FormattedMessage id="CONFIGURATION" defaultMessage="Configuration" />,
          path: 'raptor/configuration',
          permission: constants.permissionIds.aPlusEdit
        },
        siteMapping: {
          id: 'siteMappingTab',
          label: <FormattedMessage id="SITE_MAPPING" defaultMessage="Site Mapping" />,
          path: 'raptor/site-mapping',
          permission: constants.permissionIds.aPlusEdit
        },
        logs: {
          id: 'raptorLogsTab',
          label: <FormattedMessage id="RAPTOR_LOGS" defaultMessage="Logs" />,
          path: 'raptor/logs',
          permission: constants.permissionIds.aPlusEdit
        },
      }
    },
    singlewire: {
      id: 'singlewireTab',
      label: <FormattedMessage id="SINGLEWIRE" defaultMessage="Singlewire" />,
      path: 'singlewire',
      permission: constants.permissionIds.aPlusEdit,
      icon: RaptorIcon,
      requiresLicense: 'AlertPlus',
      submenu: {
        configuration: {
          id: 'singlewireConfigurationTab',
          label: <FormattedMessage id="CONFIGURATION" defaultMessage="Configuration" />,
          path: 'singlewire/configuration',
          permission: constants.permissionIds.aPlusEdit
        },
        siteMapping: {
          id: 'singlewireSiteMappingTab',
          label: <FormattedMessage id="SITE_MAPPING" defaultMessage="Site Mapping" />,
          path: 'singlewire/site-mapping',
          permission: constants.permissionIds.aPlusEdit
        },
        logs: {
          id: 'singlewireLogsTab',
          label: <FormattedMessage id="RAPTOR_LOGS" defaultMessage="Logs" />,
          path: 'singlewire/logs',
          permission: constants.permissionIds.aPlusEdit
        },
      }
    },
    orchestrate: {
      id: 'orchestrateTab',
      label: <FormattedMessage id="ORCHESTRATE" defaultMessage="Orchestrate" />,
      path: 'orchestrate',
      permission: constants.permissionIds.ccServerEdit,
      icon: OrchestrateIcon,
      requiresLicense: 'Orchestrate'
    },
    messageManager: {
      id: 'messageManagerTab',
      label: <FormattedMessage id="MESSAGE_MANAGER" defaultMessage="Message Manager" />,
      path: 'message-manager',
      permission: [constants.permissionIds.msgManagerEdit, constants.permissionIds.msgManagerFrontDeskView],
      icon: MessageManagerIcon,
      requiresLicense: 'MessageManager',
      submenu: {
        userManagement: {
          id: 'messageManagerUsersTab',
          label: <FormattedMessage id="SBMM_USER_MANAGEMENT" defaultMessage="User Management" />,
          path: 'message-manager/users',
          permission: constants.permissionIds.msgManagerEdit
        },
        groupManagement: {
          id: 'messageManagerGroupsTab',
          label: <FormattedMessage id="SBMM_GROUP_MANAGEMENT" defaultMessage="Group Management" />,
          path: 'message-manager/group-management',
          permission: constants.permissionIds.msgManagerEdit
        },
        logs: {
          id: 'messageManagerLogsTab',
          label: <FormattedMessage id="SBMM_LOGS" defaultMessage="Logs" />,
          path: 'message-manager/logs',
          permission: constants.permissionIds.msgManagerEdit
        },
        status: {
          id: 'messageManagerStatusTab',
          label: <FormattedMessage id="SBMM_STATUS" defaultMessage="Status" />,
          path: 'message-manager/status',
          permission: constants.permissionIds.msgManagerFrontDeskView
        },
        createMessage: {
          id: 'messageManagerCreateMessageTab',
          label: <FormattedMessage id="SBMM_CREATE_MESSAGE" defaultMessage="Create Message" />,
          path: 'message-manager/create-message',
          permission: constants.permissionIds.msgManagerFrontDeskView
        }
      }
    },
    signagePlus:{
      id: 'signagePlusTab',
      label: <FormattedMessage id="SIGNAGE_PLUS" defaultMessage="Signage Plus" />,
      path: 'signage-plus',
      permission: [constants.permissionIds.signagePlusEdit, constants.permissionIds.signagePlusOwner, constants.permissionIds.signagePlusTeacher],
      icon: SignagePlusIcon,
      requiresLicense: 'SignagePlus',
      submenu: {
        AdminDashboard:
        {
          id: 'signagePlusAdminDashboardTab',
          label: <FormattedMessage id="DASHBOARD-ADMIN" defaultMessage="Dashboard - Admin" />,
          path: 'signage-plus/dashboard-admin',
          permission: [constants.permissionIds.signagePlusEdit]
        },
        Dashboard:
        {
          id: 'signagePlusDashboardTab',
          label: <FormattedMessage id="DASHBOARD-OWNER" defaultMessage="Dashboard - Owner" />,
          path: 'signage-plus/dashboard-owner',
          permission: [constants.permissionIds.signagePlusOwner]
        },
        TeacherDashboard:
        {
          id: 'signagePlusTeacherDashboardTab',
          label: <FormattedMessage id="DASHBOARD-TEACHER" defaultMessage="Dashboard - Teacher" />,
          path: 'signage-plus/dashboard-teacher',
          permission: [constants.permissionIds.signagePlusTeacher]
        },
        userManagement: {
          id: 'signagePlusUsersTab',
          label: <FormattedMessage id="SBMM_USER_MANAGEMENT" defaultMessage="User Management" />,
          path: 'signage-plus/users',
          permission: constants.permissionIds.signagePlusEdit
        },
        groupManagement: {
          id: 'signagePlusGroupsTab',
          label: <FormattedMessage id="SBMM_GROUP_MANAGEMENT" defaultMessage="Group Management" />,
          path: 'signage-plus/group-management',
          permission: constants.permissionIds.signagePlusEdit
        },
        logs: {
          id: 'signagePlusLogsTab',
          label: <FormattedMessage id="SBMM_LOGS" defaultMessage="Logs" />,
          path: 'signage-plus/logs',
          permission: constants.permissionIds.signagePlusEdit
        },
        createSignage: {
          id: 'signagePlusCreateSignageTab',
          label: <FormattedMessage id="CREATE_SIGNAGEE" defaultMessage="Create Signage" />,
          path: 'signage-plus/create-signage',
          permission: [constants.permissionIds.signagePlusOwner, constants.permissionIds.signagePlusTeacher]
        },
        drafts: {
          id: 'signagePlusDraftsTab',
          label: <FormattedMessage id="DRAFTS" defaultMessage="Drafts" />,
          path: 'signage-plus/drafts',
          permission: [constants.permissionIds.signagePlusOwner, constants.permissionIds.signagePlusTeacher]
        },
        createPlaylist: {
          id: 'signagePlusCreatePlaylistTab',
          label: <FormattedMessage id="SIGNAGE_CREATE_PLAYLIST" defaultMessage="Create Playlist" />,
          path: 'signage-plus/create-playlist',
          permission: [constants.permissionIds.signagePlusOwner, constants.permissionIds.signagePlusTeacher]
        },
        mediaLibrary: {
          id: 'signagePlusMediaLibraryTab',
          label: <FormattedMessage id="MediaLibrary" defaultMessage="Media Library" />,
          path: 'signage-plus/media-library',
          permission: [constants.permissionIds.signagePlusOwner, constants.permissionIds.signagePlusTeacher]
        }
      }
    },
    trialProvisioning: {
      id: 'trialProvisioningTab',
      label: <FormattedMessage id="TRIAL_PROVISIONING" defaultMessage="Trial Provisioning" />,
      path: 'trial-provisioning',
      permission: constants.permissionIds.support,
      icon: TrialProvisioningIcon
    },
    support: {
      id: 'supportTab',
      label: <FormattedMessage id="SUPPORT" defaultMessage="Support" />,
      path: 'support',
      permission: null,
      icon: SupportIcon
    },
    miscellaneous: {
      id: 'miscellaneousTab',
      label: <FormattedMessage id="MISCELLANEOUS" defaultMessage="Miscellaneous" />,
      path:'support-activate',
      permission: constants.permissionIds.support,
      icon: MiscellaneousIcon
    }
  };

  useEffect(() => {
    modalRef.current = new window.bootstrap.Modal(document.getElementById('sidebarNotificationModal'));
  }, []);

  // Check if user has license for a feature
  useEffect(() => {
    API.getLicenses()
      .then((res) => {
        const licenses = {
          AlertPlus: res.data.some(license => 
            license.feature_ids.includes('AlertPlus')),
          Orchestrate: res.data.some(license => 
            license.feature_ids.includes('Orchestrate')),
          MessageManager: res.data.some(license => 
              license.feature_ids.includes('MessageManager')),
          SignagePlus: res.data.some(license => 
              license.feature_ids.includes('SignagePlus')),
        };
        setFeatureLicenses(licenses);

        const data = res.data.map(item => {
          const { cmses, ...rest } = item;
          return rest;
        });
        props.dispatch(fetchLicensesSuccess(data));
      })
      .catch(console.error);
  }, [JSON.stringify(licenses)]);

  /**
   * Set selected menu item based on URL path
   */
  useEffect(() => {
    if (location && location.pathname) {
      let path = location.pathname;
      // Remove leading slash, but keep internal slashes for nested paths
      if (path.startsWith('/')) {
        path = path.substring(1);
      }
      setSelected(path || '');
    }
  }, [location.pathname]);

  const hasPermission = (requiredPermissions) => {
    if (!requiredPermissions) return true;
    if (Array.isArray(requiredPermissions)) {
      return requiredPermissions.some(perm => permissionIds.includes(perm));
    }
    return permissionIds.includes(requiredPermissions);
  };

  const handleClickEvent = (e, path) => {
    e.preventDefault();
    if (signage.hasUnsavedChanges && path !== "signage-plus/create-signage" && selected === "signage-plus/create-signage") {
      setNotificationProps((prev) => ({
        icon: ScheduledMessages,
        id: 'sidebarNotificationModal',
        message: 'SIGNAGE_SCHEDULE_MESSAGE',
        description: 'SIGNAGE_SCHEDULE_WARNING',
        primaryButtonText: 'EXIT',
        secondaryButtonText: 'SCHEDULE_SIGNAGE',
        showSecondaryButton: true,
        onClose: () => { modalRef.current.hide() },
        onSubmit: () => {
          modalRef.current.hide();
          props.dispatch(setUnsavedChanges(false));
          setSelected(path);
          navigate('/' + path);
          window.localStorage.setItem('selected', path);
        },
      }));
      modalRef.current.show();
      return;
    };
    if (signage.hasUnsavedChanges && path !== "signage-plus/create-playlist" && selected === "signage-plus/create-playlist") {
      setNotificationProps((prev) => ({
        icon: Save,
        id: 'sidebarNotificationModal',
        message: 'CONFIRM_SAVE',
        description: 'CONFIRM_SAVE_BEFORE_EXIT',
        primaryButtonText: 'CONTINUE',
        secondaryButtonText: 'CANCEL',
        showSecondaryButton: true,
        onClose: () => { modalRef.current.hide() },
        onSubmit: () => {
          modalRef.current.hide();
          props.dispatch(setUnsavedChanges(false));
          setSelected(path);
          navigate('/' + path);
          window.localStorage.setItem('selected', path);
        },
      }));
      modalRef.current.show();
      return;
    };

    setSelected(path);
    navigate('/' + path);
    window.localStorage.setItem('selected', path);
  };

  const handleCollapseClick = () => {
    document.getElementById('sidebar').classList.toggle('sidebar-collapse');
    setIsSidebarOpen(!isSidebarOpen);
  };

  // Filter visible menu items based on permissions, licenses, and submenu logic
  const visibleMenuItems = Object.values(menuStructure).filter((item) => {
    // Check if the item requires a license and the user has it
    if (item.requiresLicense && !featureLicenses[item.requiresLicense]) {
      return false;
    }

    // Check permissions for the main item
    const hasMainPermission = hasPermission(item.permission);
    if (!hasMainPermission) {
      return false;
    }

    // If the item has no submenu, return based on the main permission
    if (!item.submenu) {
      return hasMainPermission;
    }

    // If the item has a submenu, check if the user has permission for any submenu item
    const visibleSubmenuItems = Object.values(item.submenu).filter((subItem) =>
      hasPermission(subItem.permission)
    );

    // The parent item is visible if the user has permission for any submenu item
    return visibleSubmenuItems.length > 0;
  });

  return (
    <>
      <div className="sidebar d-flex flex-column justify-content-between" id="sidebar">
        <ul className="nav flex-column">
          {visibleMenuItems.map((item) => {
            // Filter submenu items based on permissions
            const filteredSubmenu = item.submenu
              ? Object.values(item.submenu).filter((subItem) =>
                hasPermission(subItem.permission)
              )
              : null;

            return (
              <MenuItem
                key={item.id}
                item={{ ...item, submenu: filteredSubmenu }}
                selected={selected}
                handleClickEvent={handleClickEvent}
                isSidebarOpen={isSidebarOpen}
              />
            );
          })}
        </ul>
        <div className="px-3 py-4 text-nowrap">
          {isSidebarOpen && (
            <a className="app-library" href="https://ecomm.screenbeam.com" target="_blank" rel="noopener noreferrer">
              <FormattedMessage id="APPLICATION_LIBRARY" defaultMessage="Applications Library" />
            </a>
          )}
          <a className="app-library" href="https://ecomm.screenbeam.com" target="_blank" rel="noopener noreferrer">
            <img src="/images/cloud-link.svg" alt="" className="sidebar-icon ps-2" />
          </a>
        </div>
        <div className="mt-auto w-100 d-flex collapseBtn">
          <div className="btn-group collapseBtn" onClick={handleCollapseClick}>
            {isSidebarOpen ? <CollapseIcon className="collapseIcon" /> : <ExpandIcon className="collapseIcon" />}
          </div>
        </div>
      </div>
      <NotificationModal {...notificationProps}/>
    </>
  );  
};

const mapStateToProps = (state) => ({
  user: state.user,
  licenses: state.licenses,
  signage: state.signage
});

export default connect(mapStateToProps)(Sidebar);
